/**
 * BeCoChainVerifierCard — Supplementary Task S2.
 *
 * Super-admin dashboard card that lists the top 20 users by growing
 * cryptographic audit-chain length (`telemetry_audit_logs` row count).
 * Clicking a row runs `verify_chain_for_user` and surfaces the result
 * in an inline drawer so the admin can spot tampering at a glance.
 *
 * Endpoints:
 *   GET /api/beco/chain-leaders?limit=20 → top-N feed
 *   GET /api/beco/verify-chain/{user_id} → O(n) chain replay
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { Jargon } from "@/components/admin/JargonTerm";

function tierBadgeColor(tier) {
    if (["advanced", "moe_institution", "cma_institution"].includes(tier)) {
        return "bg-primary/10 text-primary border-primary/30";
    }
    return "bg-muted text-muted-foreground border-border";
}

function VerifyRow({ row, onVerified }) {
    const [busy, setBusy] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [result, setResult] = useState(null);

    async function verify() {
        setBusy(true);
        try {
            const { data } = await api.get(`/beco/verify-chain/${row.user_id}`);
            setResult(data);
            onVerified?.(row.user_id, data);
            if (data.valid) {
                toast.success(`${row.email || row.user_id} · chain valid (${data.rows_checked} rows)`);
            } else {
                toast.error(`Chain broken at ${data.broken_at_ts?.slice(0, 19)} (${data.rows_checked} rows)`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        } finally { setBusy(false); }
    }

    async function repair() {
        setRepairing(true);
        try {
            const { data } = await api.post(`/beco/repair-chain/${row.user_id}`);
            if (data.repaired) {
                toast.success(`Repaired · ${data.forks_collapsed} forks / ${data.losers_pruned} losers pruned`);
                // Re-verify automatically so the badge flips green.
                await verify();
            } else {
                toast(`No fork detected — chain already clean.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Repair failed");
        } finally { setRepairing(false); }
    }

    const validBadge = result?.valid === true
        ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30" variant="outline"><ShieldCheck className="w-3 h-3 mr-1" /> Valid · {result.rows_checked}</Badge>
        : result?.valid === false
            ? <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/30" variant="outline"><ShieldAlert className="w-3 h-3 mr-1" /> Broken @ {result.broken_at_ts?.slice(11, 19)} · {result.rows_checked}</Badge>
            : null;

    return (
        <tr className="border-b border-border/50" data-testid={`beco-chain-leader-${row.user_id}`}>
            <td className="py-1.5 pr-3 text-xs font-mono truncate max-w-[180px]" title={row.email || row.user_id}>
                {row.email || row.user_id.slice(0, 8) + "…"}
            </td>
            <td className="py-1.5 pr-3">
                <Badge variant="outline" className={tierBadgeColor(row.tier)}>{row.tier || "—"}</Badge>
            </td>
            <td className="py-1.5 pr-3 text-xs text-center">
                {row.opt_in === false
                    ? <span className="text-rose-400">opt-out</span>
                    : <span className="text-emerald-400">opt-in</span>}
            </td>
            <td className="py-1.5 pr-3 mono proportional-nums font-semibold">{row.chain_rows}</td>
            <td className="py-1.5 pr-3 text-[11px] font-mono text-muted-foreground">
                {row.last_ts?.slice(0, 19).replace("T", " ") ?? "—"}
            </td>
            <td className="py-1.5 pr-3 text-[10px] font-mono text-muted-foreground truncate max-w-[120px]" title={row.chain_tip}>
                {row.chain_tip?.slice(0, 10) ?? "—"}…
            </td>
            <td className="py-1.5 pr-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={verify}
                        disabled={busy}
                        data-testid={`beco-chain-verify-${row.user_id}`}
                    >
                        <ShieldCheck className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} />
                        {busy ? "…" : "Verify"}
                    </Button>
                    {result?.valid === false && (
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={repair}
                            disabled={repairing}
                            data-testid={`beco-chain-repair-${row.user_id}`}
                        >
                            <ShieldAlert className={`w-3 h-3 mr-1 ${repairing ? "animate-spin" : ""}`} />
                            {repairing ? "…" : "Repair"}
                        </Button>
                    )}
                    {validBadge}
                </div>
            </td>
        </tr>
    );
}

export default function BeCoChainVerifierCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/beco/chain-leaders?limit=20");
            setRows(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load chain leaders");
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    return (
        <Card data-testid="beco-chain-verifier-card">
            <CardHeader className="flex-row items-center justify-between space-y-0">
                <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Link2 className="w-5 h-5 text-primary" />
                        <Jargon term="audit_chain" /> leaders
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                        Top 20 users by cryptographic-chain row count. Click Verify to run an O(n) replay of the SHA-256 chain.
                    </p>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={load}
                    disabled={loading}
                    data-testid="beco-chain-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </CardHeader>
            <CardContent className="overflow-auto">
                {loading && <div className="text-sm text-muted-foreground py-3">Loading chain leaders…</div>}
                {!loading && rows.length === 0 && (
                    <div className="text-sm text-muted-foreground py-3">
                        No audit-chain rows yet — chain writes appear as users opt in or the compute cron runs.
                    </div>
                )}
                {!loading && rows.length > 0 && (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-muted-foreground border-b border-border">
                                <th className="py-2 pr-3">User</th>
                                <th className="py-2 pr-3">Tier</th>
                                <th className="py-2 pr-3">Consent</th>
                                <th className="py-2 pr-3">Chain rows</th>
                                <th className="py-2 pr-3">Last TS (UTC)</th>
                                <th className="py-2 pr-3">Chain tip</th>
                                <th className="py-2 pr-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <VerifyRow key={r.user_id} row={r} />
                            ))}
                        </tbody>
                    </table>
                )}
            </CardContent>
        </Card>
    );
}
